import { InMemoryCache } from '@apollo/client';
import { relayStylePagination } from '@apollo/client/utilities';

export const cache: InMemoryCache = new InMemoryCache({
  typePolicies: {
    /**
     * 🔴 ТЗ 0071: навык ПРОФЕССИИ и узел КАРТЫ не нормализуются.
     *
     * У обоих есть `id` навыка (`42` и `skill:42`), и Apollo по умолчанию
     * складывает их в ОДНУ запись кеша на все профессии. А поля у них
     * относятся к паре «профессия × навык»: обязательность, требуемый уровень,
     * соответствие. Python — ключевой навык в одной профессии и дополнительный
     * в другой, и последний пришедший трек перетирал остальные.
     *
     * На стенде это выглядело так: сервер отдавал для DevOps «соответствует 3
     * из 18», а страница профессии рисовала шесть навыков в группе
     * «соответствует» — чужие статусы из соседних профессий. Ни tsc, ни сборка,
     * ни тесты компонентов с моком одной профессии этого не видят.
     * Сторож — `cache/__tests__/cacheIdentity.test.ts`.
     */
    ProfessionSkillType: { keyFields: false },
    CareerSkillNodeType: { keyFields: false },
    /**
     * ТЗ 0073: соответствие навыков не нормализуется (у него нет id) и
     * заменяется целиком. Лента и панель деталей отдают его одинаково, но
     * слияние по полям оставило бы список прошлого ответа рядом с числом
     * нового.
     */
    VacancyType: { fields: { skillMatch: { merge: false } } },
    Query: {
      fields: {
        // Filter args are keyArgs: each filter combination keeps its own paginated list,
        // so changing filters swaps the list instead of merging pages across filters.
        allGroupsForLms: relayStylePagination([
          'active',
          'finished',
          'search',
          'courseId',
          'groupIds',
          'lessonDate',
        ]),

        /**
         * The ranked vacancy feed (spec 0035). Not `relayStylePagination`:
         * that helper owns `pageInfo` and drops the fields this connection
         * adds — `totalCount`, `totalCountCapped` and, critically,
         * `relevanceApplied`, which the page reads before it says anything
         * about matching.
         *
         * ⚠️ EVERY filter belongs in `keyArgs`, `sort` included. Miss one and
         * a filtered page-2 gets merged into an unfiltered page-1 — the class
         * of bug this project already hit with keyArgs strings once.
         * `first`/`after` are deliberately absent: they identify a PAGE, not a
         * list, and listing them would give every page its own cache entry and
         * defeat the merge entirely.
         */
        careerVacancyFeed: {
          keyArgs: [
            'directionId',
            'skillIds',
            'cityId',
            'workMode',
            'seniority',
            'employmentType',
            'salaryFrom',
            'freshness',
            'search',
            'savedOnly',
            'sort',
            // ТЗ 0073: режим подбора — тоже отдельный список. Без них GOAL,
            // PROFILE и ALL делили одну запись, и после переключения режима
            // до ответа сети висели строки и порядок прошлого режима.
            'matchMode',
            'trackCode',
          ],
          merge(existing, incoming, { args }) {
            // No cursor ⇒ this is a fresh first page (a refetch or a filter
            // change), so replace rather than append; otherwise a background
            // `cache-and-network` poll would duplicate the whole list.
            if (!args?.after || !existing) return incoming;
            return {
              ...incoming,
              edges: [...(existing.edges ?? []), ...(incoming.edges ?? [])],
            };
          },
        },
      },
    },
  },
});
